import logError from 'src/data/services/logError';
import React, { useState } from 'react';
import { Keyboard, Platform, StyleSheet, Text, View } from 'react-native';
import errorHandler from 'src/core/utils/errorHandler';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import PasswordField from 'src/presentation/components/atoms/PasswordField';
import SubmitButton from 'src/presentation/components/atoms/SubmitButton';
import Translations from 'src/core/utils/Translations';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import type Constants from 'src/core/utils/Constants';

interface ChangePasswordFormProps {
	route?: RouteProp<RootStackParamList, typeof Constants.screenChangePassword>;
}

function ChangePasswordForm({ route }: ChangePasswordFormProps) {
	const authStore = useAuthenticationStore();
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const [password, setPassword] = useState<string>('');
	const [passwordRepeat, setPasswordRepeat] = useState<string>('');
	const [validPassword, setValidPassword] = useState<boolean>(true);

	const isIos = Platform.OS === 'ios';
	const passwordMatch = !passwordRepeat.length || password === passwordRepeat;

	const onPasswordBlur = () => {
		if (!isIos) {
			setValidPassword(!password.length || password.length > 7);
		}
	};

	const onSubmit = () => {
		if (isIos) {
			if (password.length && password === passwordRepeat) {
				const token = route?.params?.token ?? '';
				authStore.resetPassword(token, password).catch(logError);
				navigation.goBack();
			} else {
				errorHandler(Translations.errorPasswordsNotMatchingTitle, Translations.errorPasswordsNotMatchingMessage);
			}
			return;
		}

		if (!password.length) {
			setValidPassword(false);
			return;
		}

		if (password.length && validPassword && password === passwordRepeat) {
			const token = route?.params?.token ?? '';
			authStore.resetPassword(token, password).catch(logError);
			navigation.goBack();
		}

		Keyboard.dismiss();
	};

	return (
		<View>
			<View>
				<PasswordField
					label={Translations.labelPassword}
					placeholder={Translations.placeholderPassword}
					value={password}
					onBlur={onPasswordBlur}
					onChangeText={setPassword}
				/>
				{!isIos && !validPassword && <Text style={styles.inlineError}>{Translations.errorPasswordTooShortMessage}</Text>}
			</View>

			<View>
				<PasswordField
					label={Translations.labelPasswordRepeat}
					placeholder={Translations.placeholderPasswordRepeat}
					value={passwordRepeat}
					onChangeText={setPasswordRepeat}
				/>
				{!isIos && !passwordMatch && <Text style={styles.inlineError}>{Translations.errorPasswordsNotMatchingInline}</Text>}
			</View>

			<SubmitButton
				isLoading={authStore.isLoading}
				label={Translations.buttonChangePassword}
				style={isIos ? styles.submitIos : styles.submitAndroid}
				onPress={onSubmit}
			/>
		</View>
	);
}

export default ChangePasswordForm;

const styles = StyleSheet.create({
	inlineError: {
		fontSize: Dimens.fontSize12,
		color: Colors.red,
	},
	submitIos: {
		marginTop: Dimens.margin32,
		flexDirection: 'row',
		justifyContent: 'center',
	},
	submitAndroid: {
		flexDirection: 'row',
		justifyContent: 'center',
		paddingTop: Dimens.padding40,
	},
});
